import React, { useContext } from "react";
import { LanguageContext } from "@/App";

const About = () => {
  const { language } = useContext(LanguageContext);

  const content = {
    en: {
      title: "About Sri Siddhachal Sthulabhadra Dham",
      intro: "Welcome to Sri Siddhachal Sthulabhadra Jain Teerth Dham",
      description:
        "Sri Siddhachal Sthulabhadra Jain Teerth Dham, located in the serene Chandra Hills, is a sacred pilgrimage site dedicated to Jain devotees. This spiritual haven offers a unique blend of ancient Jain traditions, stunning architecture, and peaceful surroundings that inspire devotion and inner peace.",
      historyTitle: "Our History",
      historyText:
        "The Teerth Dham has been a beacon of Jain spirituality for decades, attracting pilgrims from across the country. Built with devotion and maintained with care, it stands as a testament to the enduring faith and dedication of the Jain community.",
      facilitiesTitle: "Our Facilities",
      facility1: "Sacred Temples and Shrines",
      facility2: "Dharamshala with 45 Rooms and 3 Halls",
      facility3: "Bhojanshala for Sattvic Meals",
      facility4: "Meditation and Prayer Spaces",
      facility5: "Event Spaces for Religious Gatherings",
      missionTitle: "Our Mission",
      missionText:
        "Our mission is to provide a sacred space where devotees can connect with their faith, experience spiritual growth, and participate in the rich traditions of Jainism. We strive to maintain the highest standards of purity, devotion, and service to all pilgrims.",
    },
    hi: {
      title: "श्री सिद्धाचल स्थूलभद्र धाम के बारे में",
      intro: "श्री सिद्धाचल स्थूलभद्र जैन तीर्थ धाम में आपका स्वागत है",
      description:
        "श्री सिद्धाचल स्थूलभद्र जैन तीर्थ धाम, शांत चंद्र हिल्स में स्थित, जैन भक्तों को समर्पित एक पवित्र तीर्थ स्थल है। यह आध्यात्मिक स्थल प्राचीन जैन परंपराओं, आश्चर्यजनक वास्तुकला और शांतिपूर्ण परिवेश का एक अनूठा मिश्रण प्रदान करता है जो भक्ति और आंतरिक शांति को प्रेरित करता है।",
      historyTitle: "हमारा इतिहास",
      historyText:
        "तीर्थ धाम दशकों से जैन आध्यात्मिकता का प्रतीक रहा है, जो देश भर से तीर्थयात्रियों को आकर्षित करता है। भक्ति के साथ बनाया गया और देखभाल के साथ बनाए रखा गया, यह जैन समुदाय की स्थायी आस्था और समर्पण का प्रमाण है।",
      facilitiesTitle: "हमारी सुविधाएं",
      facility1: "पवित्र मंदिर और तीर्थस्थल",
      facility2: "45 कमरों और 3 हॉल के साथ धर्मशाला",
      facility3: "सात्विक भोजन के लिए भोजनशाला",
      facility4: "ध्यान और प्रार्थना स्थान",
      facility5: "धार्मिक सभाओं के लिए कार्यक्रम स्थान",
      missionTitle: "हमारा मिशन",
      missionText:
        "हमारा मिशन एक पवित्र स्थान प्रदान करना है जहां भक्त अपने विश्वास से जुड़ सकें, आध्यात्मिक विकास का अनुभव कर सकें और जैन धर्म की समृद्ध परंपराओं में भाग ले सकें। हम सभी तीर्थयात्रियों के लिए पवित्रता, भक्ति और सेवा के उच्चतम मानकों को बनाए रखने का प्रयास करते हैं।",
    },
  };

  const t = content[language];

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <section className="hero-section py-20 px-4" data-testid="about-hero">
        <div className="container mx-auto max-w-5xl text-center">
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold gradient-text mb-6"
            data-testid="about-title"
          >
            {t.title}
          </h1>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-5xl">
          <h2
            className="text-3xl md:text-4xl font-bold text-amber-900 mb-6"
            data-testid="about-intro"
          >
            {t.intro}
          </h2>
          <p className="text-base md:text-lg text-amber-800 leading-relaxed mb-12">
            {t.description}
          </p>

          <div className="section-divider"></div>

          <h2
            className="text-3xl md:text-4xl font-bold text-amber-900 mb-6"
            data-testid="history-title"
          >
            {t.historyTitle}
          </h2>
          <p className="text-base md:text-lg text-amber-800 leading-relaxed mb-12">
            {t.historyText}
          </p>

          <div className="section-divider"></div>

          <h2
            className="text-3xl md:text-4xl font-bold text-amber-900 mb-6"
            data-testid="facilities-title"
          >
            {t.facilitiesTitle}
          </h2>
          <ul className="space-y-4 mb-12">
            <li className="flex items-start space-x-3">
              <span className="text-amber-700 text-xl">✦</span>
              <span className="text-base md:text-lg text-amber-800">
                {t.facility1}
              </span>
            </li>
            <li className="flex items-start space-x-3">
              <span className="text-amber-700 text-xl">✦</span>
              <span className="text-base md:text-lg text-amber-800">
                {t.facility2}
              </span>
            </li>
            <li className="flex items-start space-x-3">
              <span className="text-amber-700 text-xl">✦</span>
              <span className="text-base md:text-lg text-amber-800">
                {t.facility3}
              </span>
            </li>
            <li className="flex items-start space-x-3">
              <span className="text-amber-700 text-xl">✦</span>
              <span className="text-base md:text-lg text-amber-800">
                {t.facility4}
              </span>
            </li>
            <li className="flex items-start space-x-3">
              <span className="text-amber-700 text-xl">✦</span>
              <span className="text-base md:text-lg text-amber-800">
                {t.facility5}
              </span>
            </li>
          </ul>

          <div className="section-divider"></div>

          <h2
            className="text-3xl md:text-4xl font-bold text-amber-900 mb-6"
            data-testid="mission-title"
          >
            {t.missionTitle}
          </h2>
          <p className="text-base md:text-lg text-amber-800 leading-relaxed">
            {t.missionText}
          </p>
        </div>
      </section>
    </div>
  );
};

export default About;
